/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// The lead's numbers on the lead card (owner request, PJ 2026-10-23: "prioritize owner cells with the add call the store
// instead button" / "For the ones where it's one owner in several stores, just mark this is the owner cell and he owns
// several stores. If we had called that owner cell before, just have like some sort of thing, you have called this
// before."). "Owner's line (likely cell)" is what Dial (Space) calls; "Store line" is one key away (A). Under them: "This
// owner has 3 stores in our lists: ..." and "You've called this number before: 2 calls, last Oct 21 by Max (No answer)".
// A lead with one number shows only its called-before line (the old phone-kind note stays above it). Hook-free, so
// tests/fd-owner-line.test.ts renders it; the pragmas above let Vitest compile it.
import type { QueueItem } from '../../../src/shared/api';
import type { QueueItemNumber } from '../../../src/shared/api-numbers';
import { numberLabel } from '../../../src/shared/api-numbers';
import { fmtPhone } from './labels';
import { calledBeforeLine, ownerStoresLine } from './numbers';

/** One number's row: its label and number, what dials it, and (when it can't be called now) why. */
function NumberRow(props: { n: QueueItemNumber; isDefault: boolean; fmtDate?: (iso: string) => string }) {
  const { n } = props;
  const hard = !n.gate.ok && !n.gate.confirm;
  const how = props.isDefault ? (n.target === 'owner' ? 'Dial (Space) calls it first' : 'Dial (Space) calls it') : 'Call the store (A)';
  // PJ 2026-10-28: a meeting booked says so, not "called recently".
  const note = props.isDefault ? null : hard ? n.gate.reasons[0]?.message ?? "Can't be called right now."
    : n.gate.confirm?.meeting ? 'Meeting booked: A asks first.' : n.gate.confirm ? 'Called recently: A asks first.' : null;
  const before = calledBeforeLine(n.calledBefore, props.fmtDate);
  return (
    <li class={`vd-number vd-number-${n.target}`}>
      <b>{numberLabel(n)}</b>: <span class="mono">{fmtPhone(n.e164)}</span>
      <span class="muted"> · {how}</span>
      {note ? <div class={hard ? 'vd-bad' : 'muted'}>{note}</div> : null}
      {before && !props.isDefault ? <div class="muted">{before}</div> : null}
    </li>
  );
}

export function LeadNumbers(props: { item: QueueItem; fmtDate?: (iso: string) => string }) {
  const nums = props.item.numbers;
  if (!nums) return null;
  const rows = [nums.owner, nums.store].filter((n): n is QueueItemNumber => !!n);
  const stores = ownerStoresLine(nums.ownerStores);
  const before = calledBeforeLine(nums.calledBefore, props.fmtDate);
  if (!rows.length && !stores && !before) return null;
  return (
    <div class="vd-numbers" role="note" aria-label="The numbers for this lead">
      {rows.length ? (
        <ul class="vd-number-list">
          {rows.map((n) => <NumberRow n={n} isDefault={nums.defaultTarget === n.target} fmtDate={props.fmtDate} />)}
        </ul>
      ) : null}
      {stores ? <p class="vd-owner-stores">{stores}</p> : null}
      {before ? <p class="vd-called-before">{before}</p> : null}
    </div>
  );
}
